import { ClientType, PeerIdType, PeerMessage, PeerMessageType, PresenceMessageType, websocket } from '@playcastdotio/Messaging';
import { getStateStore } from '../../../stores';
import { sendMessage as hostRealTime } from '../../realtime';
import { remoteHostHandlePeerMessage } from '../../peers/remoteControl';
import { USE_SESSION_COORDINATOR, USE_MEDIA_COORDINATOR } from '../../../../config/featureFlags';
import { peerCoordinator, gameInstancePeerManager } from '../../../../coordinators/init';
import * as Sentry from '@sentry/react';

const memoryStorage = new Map<PeerIdType, { peerId: string; peerToken?: string }>();

// Add a function to store peer data for reconnection
const storePeerData = (peerIdType: PeerIdType, peerId: string, peerToken?: string) => {
  // Disable localStorage for now
  return memoryStorage.set(peerIdType, { peerId, peerToken });
  // Store peer data in localStorage for persistence across sessions
  // if (peerToken) {
  //   const storageKey = `playcast_peerdata_${peerIdType}`;
  //   try {
  //     const peerData = { peerId, peerToken, peerIdType };
  //     localStorage.setItem(storageKey, JSON.stringify(peerData));
  //     console.log(`[RemoteHost] Stored peer data for ${peerIdType}`, {
  //       peerId,
  //       tokenPreview: peerToken.substring(0, 5) + '...',
  //     });
  //   } catch (e) {
  //     console.error(`[RemoteHost] Failed to store peer data for ${peerIdType}:`, e);
  //   }
  // }
};

// Function to retrieve stored peer data
export const getStoredPeerData = (peerIdType: PeerIdType): { peerId: string; peerToken?: string } | null => {
  // Disable localStorage for now
  return memoryStorage.get(peerIdType) || null;
  // const storageKey = `playcast_peerdata_${peerIdType}`;
  // try {
  //   const data = localStorage.getItem(storageKey);
  //   if (data) {
  //     const parsedData = JSON.parse(data);
  //     console.log(`[RemoteHost] Retrieved stored peer data for ${peerIdType}:`, {
  //       peerId: parsedData.peerId,
  //       hasToken: !!parsedData.peerToken,
  //     });
  //     return parsedData;
  //   }
  // } catch (e) {
  //   console.error(`[RemoteHost] Failed to parse stored peer data for ${peerIdType}`, e);
  // }
  // return null;
};

export const handlePeerMessage = (message: PeerMessage) => {
  Sentry.logger.info(`[PeerHandler] Peer message: ${message.type}`);
  switch (message.type) {
    case PeerMessageType.PEER_GET_ID_ANSWER: {
      Sentry.logger.info(`[PeerHandler] PEER_GET_ID_ANSWER peerId=${message.peerId} type=${message.peerIdType} hasToken=${!!message.peerToken}`);

      const target = message.peerIdType;
      const state = getStateStore('ipc');
      const hostPresence = state.hostPresenceState.currentState;

      if (message.peerToken) {
        storePeerData(message.peerIdType, message.peerId, message.peerToken);
      } else {
        Sentry.logger.warn(`[PeerHandler] No token received for peer ID: ${message.peerId}`);
      }

      if (message.iceServers) {
        Sentry.logger.info(`[PeerHandler] Storing ICE servers for peer: ${message.peerId}`);
        state.iceServerState.updateState({
          ...state.iceServerState.currentState,
          remoteHostIceServers: message.iceServers,
        });
      }

      switch (target) {
        case PeerIdType.HOST: {
          const instanceIndex = (message as { peerMetadata?: { gameInstanceIndex?: number } })
            .peerMetadata?.gameInstanceIndex;

          if (instanceIndex !== undefined) {
            Sentry.logger.info(`[PeerHandler] Instance peer id for index ${instanceIndex}: ${message.peerId}`);
            gameInstancePeerManager.handlePeerIdAssigned(message.peerId, instanceIndex);
          } else {
            Sentry.logger.info(`[PeerHandler] Host peer id (v1/global): ${message.peerId}`);
            hostPresence.hostPeerId = message.peerId;

            // STRANGLER-FIG: Bridge to PeerCoordinator
            if (USE_MEDIA_COORDINATOR) {
              peerCoordinator.updateHostPeerId(message.peerId);
            }
          }
          break;
        }
        case PeerIdType.REMOTE_HOST: {
          Sentry.logger.info(`[PeerHandler] Remote host peer id: ${message.peerId}`);
          hostPresence.remoteHostPeerId = message.peerId;

          // STRANGLER-FIG: Bridge to PeerCoordinator
          if (USE_MEDIA_COORDINATOR) {
            peerCoordinator.updateRemoteHostPeerId(message.peerId);
          }
          break;
        }
        case PeerIdType.PLAYER:
        case PeerIdType.REMOTE_CLIENT: {
          Sentry.logger.warn(`[PeerHandler] Unexpected peer id for player/remote_client: ${message.peerId}`);
          break;
        }
        default: {
          Sentry.logger.warn(`[PeerHandler] Unknown peer id target: ${target}`);
          break;
        }
      }
      Sentry.logger.info(`[PeerHandler] Updated host presence: hostPeerId=${hostPresence.hostPeerId} remoteHostPeerId=${hostPresence.remoteHostPeerId}`);
      // update state
      state.hostPresenceState.updateState({ ...hostPresence });

      // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
      if (!USE_SESSION_COORDINATOR) {
        hostRealTime(
          websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
            clientType: ClientType.HOST,
            presence: hostPresence,
          }),
        );
      }

      break;
    }
    case PeerMessageType.PEER_ICE_CANDIDATE:
    case PeerMessageType.PEER_SDP_ANSWER:
    case PeerMessageType.PEER_SDP_OFFER: {
      remoteHostHandlePeerMessage(message);
      break;
    }
    case PeerMessageType.PEER_ERROR: {
      Sentry.logger.error(`[PeerHandler] PEER_ERROR received: ${JSON.stringify(message)}`);
      break;
    }
    case PeerMessageType.PEER_GET_ID:
    case PeerMessageType.PEER_ICE_COMPLETE:
    case PeerMessageType.PEER_STATS:
    case PeerMessageType.PEER_STATS_SUCCESS: {
      break;
    }
  }
};
